import { useEffect, useRef, useState } from 'react';
import ResearchPageHeader from '../components/ResearchPageHeader';
import { apiJson, isUnauthorizedError } from '../services/api';
import './Research.css';
import './ResearchDataWarehouseAudit.css';

export function formatDate(value) {
  if (!value) return '—';
  const normalized = String(value).trim();
  const dateOnlyMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
  }
  const monthYearMatch = normalized.match(/^([A-Za-z]+) (\d{4})$/);
  if (monthYearMatch) {
    const [, monthName, year] = monthYearMatch;
    const monthIndex = new Date(`${monthName} 1, 2000`).getMonth();
    if (Number.isFinite(monthIndex)) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(Number(year), monthIndex, 1)));
    }
    return normalized;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatPeriodRange(earliest, latest) {
  if (!latest) return '—';
  if (!earliest) return formatDate(latest);
  return `${formatDate(earliest)}–${formatDate(latest)}`;
}

function formatNumber(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US').format(Number(value));
}

export function pillTone(status) {
  switch (status) {
    case 'pass':
    case 'loaded':
    case 'no_change':
    case 'fresh':
    case 'completed':
      return 'good';
    case 'completed_with_errors':
      return 'warn';
    case 'high':
    case 'good':
      return 'good';
    case 'medium_high':
    case 'medium':
      return 'warn';
    case 'historical_baseline':
    case 'lagged':
    case 'running':
      return 'info';
    case 'warn':
    case 'missing_regions':
    case 'needs_review':
    case 'skipped':
    case 'no_data':
    case 'stale':
    case 'pending':
      return 'warn';
    case 'import_failed':
    case 'fail':
    case 'failed':
    case 'disabled':
    case 'error':
    case 'low':
      return 'bad';
    default:
      return 'neutral';
  }
}

function simpleStatus(dataset) {
  if (['import_failed', 'missing_regions', 'needs_review'].includes(dataset.pipelineStatus)) {
    return { label: 'Needs Attention', tone: 'needs_review' };
  }
  if (dataset.pipelineStatus === 'running') {
    return { label: 'Running', tone: 'running' };
  }
  if (dataset.pipelineStatus === 'pending') {
    return { label: 'Pending', tone: 'pending' };
  }
  if (dataset.pipelineStatus === 'disabled') {
    return { label: 'Disabled', tone: 'disabled' };
  }
  return { label: 'OK', tone: 'pass' };
}

function statusContext(dataset, status) {
  const runDate = dataset.latestRunFinishedAt || dataset.latestRunImportedAt;
  if (status.label === 'OK') {
    return runDate ? `Successful run: ${formatDate(runDate)}` : 'Latest run successful';
  }
  if (status.label === 'Needs Attention') {
    const missing = Number(dataset.regionsMissing || 0);
    if (missing > 0) {
      return `${formatNumber(missing)} region${missing === 1 ? '' : 's'} affected`;
    }
    return dataset.pipelineStatus === 'import_failed' ? 'Latest run failed' : 'Latest run needs review';
  }
  if (status.label === 'Running') {
    return dataset.latestRunStartedAt
      ? `Started: ${formatDate(dataset.latestRunStartedAt)}`
      : 'Run in progress';
  }
  if (status.label === 'Pending') return 'No completed run yet';
  if (status.label === 'Disabled') return 'Monitoring disabled';
  return '';
}

function shortTrustLabel(label) {
  return String(label || 'Unscored').replace(/\s+trust$/i, '');
}

function scheduleLabel(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'daily metadata poll; conditional release extraction') {
    return 'Daily check; pull on change';
  }
  if (normalized === 'manual review when the workbook changes') {
    return 'Check on source change';
  }
  if (normalized === 'manual until weekly activation') {
    return 'Manual';
  }
  return value || 'Schedule not set';
}

function coverageSummary(dataset) {
  if (dataset.regionsLoaded != null) {
    if (dataset.regionsRequested != null && Number(dataset.regionsRequested) > 0) {
      return {
        primary: `${formatNumber(dataset.regionsLoaded)} / ${formatNumber(dataset.regionsRequested)}`,
        secondary: Number(dataset.regionsMissing || 0) > 0
          ? `${formatNumber(dataset.regionsMissing)} missing`
          : 'all requested regions',
      };
    }
    return {
      primary: formatNumber(dataset.regionsLoaded),
      secondary: 'loaded',
    };
  }
  return {
    primary: formatNumber(dataset.rowsWritten ?? dataset.rowsPulled),
    secondary: 'rows available',
  };
}

function rowsLabel(dataset) {
  return `${formatNumber(dataset.rowsPulled)} / ${formatNumber(dataset.rowsWritten)}`;
}

function regionsLabel(dataset) {
  if (dataset.regionsLoaded == null) return '—';
  if (dataset.regionsRequested == null) return formatNumber(dataset.regionsLoaded);
  return `${formatNumber(dataset.regionsLoaded)} / ${formatNumber(dataset.regionsMissing)}`;
}

function inferWarningLevel(message, status, fallbackLabel = 'Warning') {
  const normalizedMessage = String(message || '').toLowerCase();
  const normalizedStatus = String(status || '').toLowerCase();
  if (
    normalizedMessage.includes('error')
    || normalizedMessage.includes('failed')
    || normalizedMessage.includes('fail:')
    || normalizedStatus === 'failed'
    || normalizedStatus === 'import_failed'
  ) {
    return { label: 'Error', tone: 'bad' };
  }
  if (
    normalizedMessage.includes('warn')
    || normalizedStatus === 'completed_with_errors'
    || normalizedStatus === 'needs_review'
    || normalizedStatus === 'missing_regions'
    || normalizedStatus === 'skipped'
  ) {
    return { label: 'Warn', tone: 'warn' };
  }
  if (fallbackLabel === 'Info') return { label: 'Info', tone: 'info' };
  return { label: fallbackLabel, tone: 'neutral' };
}

function summarizeAffectedScope(targetTables, fallback) {
  if (Array.isArray(targetTables) && targetTables.length) {
    if (targetTables.length === 1) return targetTables[0];
    return `${targetTables[0]} +${targetTables.length - 1} more`;
  }
  return fallback || '—';
}

function pushWarningEntry(entries, seen, entry) {
  const normalizedMessage = String(entry.message || '').trim();
  if (!normalizedMessage) return;
  const key = [
    entry.levelLabel,
    normalizedMessage,
    entry.affectedLabel || '',
    entry.runLabel || '',
    entry.timestamp || '',
  ].join('|');
  if (seen.has(key)) return;
  seen.add(key);
  entries.push({
    ...entry,
    key,
    message: normalizedMessage,
  });
}

function buildSummaryWarningEntries(dataset) {
  if (!dataset) return [];
  const affectedLabel = dataset.sourceName || '—';
  const runLabel = dataset.latestRunId || dataset.pipelineStatusLabel || 'Latest run';
  const timestamp = dataset.latestRunFinishedAt || dataset.latestRunImportedAt || dataset.lastSuccessfulRunAt || null;
  return (dataset.warningPreview || []).map((message, index) => {
    const level = inferWarningLevel(message, dataset.pipelineStatus);
    return {
      key: `${dataset.datasetKey}-summary-${index}`,
      levelLabel: level.label,
      levelTone: level.tone,
      message,
      affectedLabel,
      affectedTitle: affectedLabel,
      runLabel,
      timestamp,
    };
  });
}

function buildDetailedWarningEntries(detail) {
  if (!detail?.dataset) return [];

  const entries = [];
  const seen = new Set();
  const targetTables = Array.isArray(detail.source?.targetTables) ? detail.source.targetTables : [];
  const datasetSourceName = detail.dataset.sourceName || '—';
  const defaultAffectedLabel = summarizeAffectedScope(targetTables, datasetSourceName);
  const defaultAffectedTitle = targetTables.length ? targetTables.join(', ') : datasetSourceName;
  const latestRun = detail.recentRuns?.[0] || null;

  (detail.recentRuns || []).forEach((run) => {
    (run.warningItems || []).forEach((message) => {
      const level = inferWarningLevel(message, run.status);
      pushWarningEntry(entries, seen, {
        levelLabel: level.label,
        levelTone: level.tone,
        message,
        affectedLabel: defaultAffectedLabel,
        affectedTitle: defaultAffectedTitle,
        runLabel: run.runId || 'Latest run',
        timestamp: run.finishedAt || run.startedAt || null,
      });
    });
  });

  (detail.latestRunIssues || []).forEach((issue) => {
    const message = issue.errorMessage || issue.warningMessage;
    if (!message) return;
    const level = inferWarningLevel(message, issue.status, issue.errorMessage ? 'Error' : 'Warning');
    const affectedLabel = issue.regionName || issue.regionKey || defaultAffectedLabel;
    const affectedTitle = issue.regionName || issue.regionKey || defaultAffectedTitle;
    pushWarningEntry(entries, seen, {
      levelLabel: level.label,
      levelTone: level.tone,
      message,
      affectedLabel,
      affectedTitle,
      runLabel: latestRun?.runId || detail.dataset.latestRunId || 'Latest run',
      timestamp: latestRun?.finishedAt || detail.dataset.latestRunFinishedAt || null,
    });
  });

  (detail.warningItems || []).forEach((message) => {
    const level = inferWarningLevel(message, detail.dataset.pipelineStatus);
    pushWarningEntry(entries, seen, {
      levelLabel: level.label,
      levelTone: level.tone,
      message,
      affectedLabel: defaultAffectedLabel,
      affectedTitle: defaultAffectedTitle,
      runLabel: detail.dataset.latestRunId || latestRun?.runId || 'Latest run',
      timestamp: detail.dataset.latestRunFinishedAt || latestRun?.finishedAt || detail.dataset.lastSuccessfulRunAt || null,
    });
  });

  return entries;
}

function WarehouseBadge({ label, status }) {
  return <span className={`warehouse-badge warehouse-badge--${pillTone(status)}`}>{label}</span>;
}

function WarningLevelBadge({ label, tone }) {
  return <span className={`warehouse-warning-level warehouse-warning-level--${tone}`}>{label}</span>;
}

export default function ResearchDataWarehouseAudit() {
  const detailRequestRef = useRef(0);
  const detailCacheRef = useRef({});
  const detailPromiseRef = useRef({});
  const warningTriggerRefs = useRef({});
  const warningPopoverRef = useRef(null);
  const warningCloseTimerRef = useRef(null);
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailCache, setDetailCache] = useState({});
  const [detailLoadState, setDetailLoadState] = useState({});
  const [detailLoadErrorMap, setDetailLoadErrorMap] = useState({});
  const [selectedDatasetKey, setSelectedDatasetKey] = useState('');
  const [selectedDatasetDetail, setSelectedDatasetDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [openWarningDatasetKey, setOpenWarningDatasetKey] = useState('');
  const [pinnedWarningDatasetKey, setPinnedWarningDatasetKey] = useState('');
  const [warningPopoverStyle, setWarningPopoverStyle] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let active = true;

    async function loadSummary() {
      try {
        setLoading(true);
        setError('');
        const payload = await apiJson('/research/data-warehouse-audit?compact=true');
        if (!active) return;
        setDatasets(payload.datasets || []);
      } catch (loadError) {
        if (!active) return;
        if (isUnauthorizedError(loadError)) {
          window.location.assign('/login');
          return;
        }
        setError(loadError?.message || 'Could not load data warehouse audit data.');
      } finally {
        if (active) setLoading(false);
      }
    }

    void loadSummary();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => () => {
    if (warningCloseTimerRef.current) {
      window.clearTimeout(warningCloseTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!openWarningDatasetKey) return undefined;

    function handlePointerDown(event) {
      const trigger = warningTriggerRefs.current[openWarningDatasetKey];
      if (trigger?.contains(event.target)) return;
      if (warningPopoverRef.current?.contains(event.target)) return;
      setPinnedWarningDatasetKey('');
      setOpenWarningDatasetKey('');
      setWarningPopoverStyle(null);
    }

    function handleKeyDown(event) {
      if (event.key !== 'Escape') return;
      setPinnedWarningDatasetKey('');
      setOpenWarningDatasetKey('');
      setWarningPopoverStyle(null);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openWarningDatasetKey]);

  function clearWarningCloseTimer() {
    if (!warningCloseTimerRef.current) return;
    window.clearTimeout(warningCloseTimerRef.current);
    warningCloseTimerRef.current = null;
  }

  function setWarningTriggerRef(datasetKey, node) {
    if (node) {
      warningTriggerRefs.current[datasetKey] = node;
      return;
    }
    delete warningTriggerRefs.current[datasetKey];
  }

  function positionWarningPopover(datasetKey) {
    const trigger = warningTriggerRefs.current[datasetKey];
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const margin = 12;
    const width = Math.max(220, Math.min(360, window.innerWidth - margin * 2));
    const popoverHeight = warningPopoverRef.current?.offsetHeight || 220;
    const gap = 12;
    let placement = 'left';
    let left = rect.left - width - gap;
    let top = rect.top - 8;

    if (left < margin) {
      left = rect.right + gap;
      placement = 'right';
    }

    if (left + width > window.innerWidth - margin || left < margin) {
      left = Math.min(
        window.innerWidth - width - margin,
        Math.max(margin, rect.left + (rect.width / 2) - (width / 2)),
      );
      top = rect.bottom + gap;
      placement = 'bottom';
    }

    if (placement === 'bottom' && top + popoverHeight > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - popoverHeight - gap);
      placement = 'top';
    }

    if ((placement === 'left' || placement === 'right') && top + popoverHeight > window.innerHeight - margin) {
      top = Math.max(margin, window.innerHeight - popoverHeight - margin);
    }

    setWarningPopoverStyle({
      top,
      left,
      width,
      placement,
    });
  }

  useEffect(() => {
    if (!openWarningDatasetKey) return undefined;

    function updatePosition() {
      positionWarningPopover(openWarningDatasetKey);
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [
    openWarningDatasetKey,
    detailCache,
    detailLoadState,
  ]);

  async function loadDatasetDetail(datasetKey) {
    if (detailCacheRef.current[datasetKey]) {
      return detailCacheRef.current[datasetKey];
    }
    if (detailPromiseRef.current[datasetKey]) {
      return detailPromiseRef.current[datasetKey];
    }

    setDetailLoadState((current) => ({ ...current, [datasetKey]: true }));
    setDetailLoadErrorMap((current) => ({ ...current, [datasetKey]: '' }));

    const request = apiJson(`/research/data-warehouse-audit/${encodeURIComponent(datasetKey)}`)
      .then((payload) => {
        detailCacheRef.current[datasetKey] = payload;
        setDetailCache((current) => ({ ...current, [datasetKey]: payload }));
        return payload;
      })
      .catch((loadError) => {
        if (isUnauthorizedError(loadError)) {
          window.location.assign('/login');
          return null;
        }
        setDetailLoadErrorMap((current) => ({
          ...current,
          [datasetKey]: loadError?.message || 'Could not load dataset detail.',
        }));
        throw loadError;
      })
      .finally(() => {
        delete detailPromiseRef.current[datasetKey];
        setDetailLoadState((current) => ({ ...current, [datasetKey]: false }));
      });

    detailPromiseRef.current[datasetKey] = request;
    return request;
  }

  function primeWarningDetail(datasetKey) {
    if (!datasetKey || detailCacheRef.current[datasetKey] || detailPromiseRef.current[datasetKey]) return;
    void loadDatasetDetail(datasetKey).catch(() => {});
  }

  async function openDataset(datasetKey) {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setSelectedDatasetKey(datasetKey);
    setPinnedWarningDatasetKey('');
    setOpenWarningDatasetKey('');
    setWarningPopoverStyle(null);
    setDetailError('');

    const cachedDetail = detailCacheRef.current[datasetKey] || null;
    setSelectedDatasetDetail(cachedDetail);
    setDetailLoading(!cachedDetail);

    try {
      const payload = await loadDatasetDetail(datasetKey);
      if (detailRequestRef.current !== requestId || !payload) return;
      setSelectedDatasetDetail(payload);
    } catch (loadError) {
      if (detailRequestRef.current !== requestId) return;
      setDetailError(loadError?.message || 'Could not load dataset detail.');
    } finally {
      if (detailRequestRef.current === requestId) {
        setDetailLoading(false);
      }
    }
  }

  function closeDrawer() {
    setSelectedDatasetKey('');
    setSelectedDatasetDetail(null);
    setDetailError('');
    setDetailLoading(false);
  }

  function openWarningPopover(datasetKey) {
    if (!datasetKey) return;
    if (pinnedWarningDatasetKey && pinnedWarningDatasetKey !== datasetKey) return;
    clearWarningCloseTimer();
    setOpenWarningDatasetKey(datasetKey);
    primeWarningDetail(datasetKey);
    window.requestAnimationFrame(() => positionWarningPopover(datasetKey));
  }

  function scheduleWarningPopoverClose(datasetKey) {
    if (!datasetKey || pinnedWarningDatasetKey === datasetKey) return;
    clearWarningCloseTimer();
    warningCloseTimerRef.current = window.setTimeout(() => {
      if (pinnedWarningDatasetKey === datasetKey) return;
      setOpenWarningDatasetKey((currentKey) => (currentKey === datasetKey ? '' : currentKey));
      setWarningPopoverStyle(null);
    }, 120);
  }

  function toggleWarningPopover(datasetKey) {
    if (!datasetKey) return;
    clearWarningCloseTimer();
    primeWarningDetail(datasetKey);
    setPinnedWarningDatasetKey((currentPinnedKey) => {
      const nextPinnedKey = currentPinnedKey === datasetKey ? '' : datasetKey;
      setOpenWarningDatasetKey(nextPinnedKey);
      if (nextPinnedKey) {
        window.requestAnimationFrame(() => positionWarningPopover(datasetKey));
      } else {
        setWarningPopoverStyle(null);
      }
      return nextPinnedKey;
    });
  }

  const actionableStatuses = ['import_failed', 'missing_regions', 'needs_review'];
  const staleCount = datasets.filter((row) => row.sourceFreshness === 'stale').length;
  const reviewCount = datasets.filter((row) => actionableStatuses.includes(row.pipelineStatus)).length;
  const activeCount = datasets.filter((row) => row.active !== false).length;
  const visibleDatasets = datasets.filter((row) => {
    if (statusFilter === 'action') return actionableStatuses.includes(row.pipelineStatus);
    if (statusFilter === 'healthy') {
      return !actionableStatuses.includes(row.pipelineStatus);
    }
    return true;
  });
  const detailDataset = selectedDatasetDetail?.dataset || null;
  const latestRecentRun = selectedDatasetDetail?.recentRuns?.[0] || null;
  const rankedMarketCoverageAudits = selectedDatasetDetail?.rankedMarketCoverageAudits || [];
  const rankedMarketCoverageIssues = rankedMarketCoverageAudits.filter((audit) => audit.status !== 'loaded');
  const openWarningDataset = datasets.find((row) => row.datasetKey === openWarningDatasetKey) || null;
  const openWarningDetail = openWarningDatasetKey ? detailCache[openWarningDatasetKey] || null : null;
  const openWarningEntries = (() => {
    if (openWarningDetail) {
      const detailed = buildDetailedWarningEntries(openWarningDetail);
      if (detailed.length) return detailed;
    }
    return buildSummaryWarningEntries(openWarningDataset);
  })();
  const openWarningLoading = Boolean(
    openWarningDatasetKey
      && detailLoadState[openWarningDatasetKey]
      && !openWarningDetail,
  );
  const openWarningError = openWarningDatasetKey ? detailLoadErrorMap[openWarningDatasetKey] : '';

  return (
    <div className="research-page warehouse-page">
      <div className="research-shell warehouse-shell">
        <ResearchPageHeader
          eyebrow="Pipeline Health & Freshness"
          title="Data Warehouse Audit"
          description="Pipeline health, freshness, source trust, and recent run detail for the datasets behind Market Research. This page separates run recency from source recency so a recent import does not hide stale upstream data."
          statsCard={{
            label: 'Audit Scope',
            items: [
              { value: formatNumber(activeCount), label: 'Datasets' },
              { value: formatNumber(reviewCount), label: 'Action Needed' },
              { value: formatNumber(staleCount), label: 'Stale Upstream' },
            ],
          }}
        />

        <section className="warehouse-panel">
          <div className="warehouse-panel__head">
            <div>
              <h2 className="warehouse-panel__title">Market Research Dataset Health</h2>
              <p className="warehouse-panel__copy">
                Is each source working, current, and complete? Action Needed is reserved for failures that require intervention. Open Details for supporting findings.
              </p>
            </div>
            <div className="warehouse-panel__legend">
              <span><strong>Data through:</strong> latest source period</span>
              <span><strong>Last run:</strong> latest automation result</span>
            </div>
          </div>

          <div className="warehouse-filters" aria-label="Filter datasets by operational status">
            {[
              { key: 'all', label: 'All', count: activeCount },
              { key: 'action', label: 'Action needed', count: reviewCount },
              { key: 'healthy', label: 'Healthy', count: Math.max(activeCount - reviewCount, 0) },
            ].map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`warehouse-filter${statusFilter === filter.key ? ' warehouse-filter--active' : ''}`}
                onClick={() => setStatusFilter(filter.key)}
                aria-pressed={statusFilter === filter.key}
              >
                {filter.label} <span>{formatNumber(filter.count)}</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="warehouse-state">Loading data warehouse audit…</div>
          ) : error ? (
            <div className="warehouse-state warehouse-state--error">{error}</div>
          ) : (
            <div className="warehouse-table-wrap">
              <table className="warehouse-table">
                <thead>
                  <tr>
                    <th>Dataset</th>
                    <th>Status</th>
                    <th>Data Through</th>
                    <th>Last Run</th>
                    <th>
                      Regions
                      <span className="warehouse-table-head-note">loaded / requested</span>
                    </th>
                    <th>Freshness</th>
                    <th>Trust</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDatasets.map((dataset) => {
                    const coverage = coverageSummary(dataset);
                    const status = simpleStatus(dataset);
                    const statusNote = statusContext(dataset, status);
                    return (
                    <tr key={dataset.datasetKey}>
                      <td title={dataset.name}>
                        <div className="warehouse-cell-stack">
                          <button
                            type="button"
                            className="warehouse-link warehouse-link--clamp"
                            onClick={() => openDataset(dataset.datasetKey)}
                            title={dataset.name}
                          >
                            {dataset.name}
                          </button>
                          <div className="warehouse-cell-sub warehouse-cell-sub--truncate" title={dataset.datasetKey}>
                            {dataset.sourceName} · {dataset.geographyLevel || 'geography not set'}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="warehouse-cell-stack">
                          <WarehouseBadge label={status.label} status={status.tone} />
                          <div className="warehouse-cell-sub warehouse-status-reason" title={statusNote}>
                            {statusNote}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="warehouse-cell-stack">
                          <div
                            className="warehouse-cell-line"
                            title={`${formatDate(dataset.earliestSourcePeriod)} – ${formatDate(dataset.latestSourcePeriod)}`}
                          >
                            {formatPeriodRange(dataset.earliestSourcePeriod, dataset.latestSourcePeriod)}
                          </div>
                          {dataset.pulledThroughPeriod && dataset.pulledThroughPeriod !== dataset.latestSourcePeriod ? (
                            <div className="warehouse-cell-sub" title={`Pulled through ${formatDate(dataset.pulledThroughPeriod)}`}>
                              Pulled through {formatDate(dataset.pulledThroughPeriod)}
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="warehouse-cell-stack">
                          <div className="warehouse-cell-line warehouse-date-time" title={formatDateTime(dataset.latestRunFinishedAt || dataset.latestRunImportedAt)}>
                          {formatDateTime(dataset.latestRunFinishedAt || dataset.latestRunImportedAt)}
                          </div>
                          <div className="warehouse-cell-sub">
                            {scheduleLabel(dataset.recommendedPullFrequency || dataset.updateFrequency)}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="warehouse-cell-stack">
                          <div className="warehouse-cell-line" title={coverage.primary}>
                            {coverage.primary}
                          </div>
                          <div className="warehouse-cell-sub" title={coverage.secondary}>
                            {coverage.secondary}
                          </div>
                        </div>
                      </td>
                      <td>
                        <WarehouseBadge label={dataset.sourceFreshnessLabel} status={dataset.sourceFreshness} />
                      </td>
                      <td>
                        <div className="warehouse-trust-stack">
                          <WarehouseBadge label={shortTrustLabel(dataset.reliabilityLabel)} status={dataset.reliabilityTier} />
                        </div>
                      </td>
                      <td className="warehouse-col--details">
                        <div className="warehouse-detail-actions">
                        <button
                          type="button"
                          className="warehouse-detail-button"
                          onClick={() => openDataset(dataset.datasetKey)}
                        >
                          Open
                        </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {openWarningDatasetKey && openWarningDataset && warningPopoverStyle ? (
          <div
            ref={warningPopoverRef}
            className={`warehouse-warning-popover warehouse-warning-popover--${warningPopoverStyle.placement || 'left'}`}
            style={{
              top: warningPopoverStyle.top,
              left: warningPopoverStyle.left,
              width: warningPopoverStyle.width,
            }}
            role="dialog"
            aria-label={`${openWarningDataset.name} warnings`}
            onMouseEnter={clearWarningCloseTimer}
            onMouseLeave={() => scheduleWarningPopoverClose(openWarningDatasetKey)}
          >
            <div className="warehouse-warning-popover__head">
              <div>
                <strong>{openWarningDataset.name}</strong>
                <span>{formatNumber(openWarningDataset.warningCount)} finding{openWarningDataset.warningCount === 1 ? '' : 's'}</span>
              </div>
              {openWarningLoading ? <span className="warehouse-warning-popover__meta-flag">Loading…</span> : null}
            </div>

            {openWarningEntries.length ? (
              <div className="warehouse-warning-popover__list">
                {openWarningEntries.map((entry) => (
                  <div key={entry.key} className="warehouse-warning-item">
                    <div className="warehouse-warning-item__top">
                      <WarningLevelBadge label={entry.levelLabel} tone={entry.levelTone} />
                      <span className="warehouse-warning-item__scope" title={entry.affectedTitle || entry.affectedLabel}>
                        {entry.affectedLabel || '—'}
                      </span>
                    </div>
                    <p className="warehouse-warning-item__message">{entry.message}</p>
                    {(entry.runLabel || entry.timestamp) ? (
                      <div className="warehouse-warning-item__foot">
                        {entry.runLabel ? (
                          <span className="warehouse-warning-item__run" title={entry.runLabel}>
                            {entry.runLabel}
                          </span>
                        ) : null}
                        {entry.timestamp ? (
                          <span className="warehouse-warning-item__time" title={formatDateTime(entry.timestamp)}>
                            {formatDateTime(entry.timestamp)}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="warehouse-warning-popover__state">No finding detail recorded for the latest run.</div>
            )}

            {openWarningError ? (
              <div className="warehouse-warning-popover__footer warehouse-warning-popover__footer--error">
                {openWarningError}
              </div>
            ) : openWarningLoading ? (
              <div className="warehouse-warning-popover__footer">
                Fetching full warning detail…
              </div>
            ) : null}
          </div>
        ) : null}

        {(selectedDatasetKey || detailLoading || detailError) ? (
          <div className="warehouse-drawer-layer" role="presentation" onClick={closeDrawer}>
            <aside
              className="warehouse-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Dataset audit detail"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="warehouse-drawer__head">
                <div>
                  <p className="warehouse-drawer__eyebrow">Dataset Detail</p>
                  <h2>{detailDataset?.name || selectedDatasetKey}</h2>
                  <p>{detailDataset?.sourceName || 'Loading source detail…'}</p>
                </div>
                <button type="button" className="warehouse-drawer__close" onClick={closeDrawer}>
                  <i className="pi pi-times" />
                </button>
              </div>

              {detailLoading ? (
                <div className="warehouse-drawer__state">Loading dataset detail…</div>
              ) : detailError ? (
                <div className="warehouse-drawer__state warehouse-drawer__state--error">{detailError}</div>
              ) : detailDataset ? (
                <div className="warehouse-drawer__body">
                  <div className="warehouse-drawer__badges">
                    <WarehouseBadge label={detailDataset.pipelineStatusLabel} status={detailDataset.pipelineStatus} />
                    <WarehouseBadge label={detailDataset.sourceFreshnessLabel} status={detailDataset.sourceFreshness} />
                    <WarehouseBadge label={detailDataset.reliabilityLabel} status={detailDataset.reliabilityTier} />
                  </div>

                  <div className="warehouse-stat-grid">
                    <div className="warehouse-stat-card">
                      <span>Last successful run</span>
                      <strong>{formatDateTime(detailDataset.lastSuccessfulRunAt)}</strong>
                    </div>
                    <div className="warehouse-stat-card">
                      <span>Last failed run</span>
                      <strong>{formatDateTime(detailDataset.latestFailedRunAt)}</strong>
                    </div>
                    <div className="warehouse-stat-card">
                      <span>Latest source period</span>
                      <strong>{formatDate(detailDataset.latestSourcePeriod)}</strong>
                    </div>
                    <div className="warehouse-stat-card">
                      <span>Rows pulled / written</span>
                      <strong>{rowsLabel(detailDataset)}</strong>
                    </div>
                    <div className="warehouse-stat-card">
                      <span>{detailDataset.regionsRequested == null ? 'Regions loaded' : 'Regions loaded / missing'}</span>
                      <strong>{regionsLabel(detailDataset)}</strong>
                    </div>
                  </div>

                  <section className="warehouse-detail-section">
                    <h3>Source & Job</h3>
                    <div className="warehouse-detail-grid">
                      <div>
                        <span>Source URL</span>
                        {selectedDatasetDetail.source.sourceUrl ? (
                          <a href={selectedDatasetDetail.source.sourceUrl} target="_blank" rel="noreferrer">
                            {selectedDatasetDetail.source.sourceUrl}
                          </a>
                        ) : (
                          <strong>—</strong>
                        )}
                      </div>
                      {selectedDatasetDetail.source.documentationUrl && selectedDatasetDetail.source.documentationUrl !== selectedDatasetDetail.source.sourceUrl ? (
                        <div>
                          <span>Source docs</span>
                          <a href={selectedDatasetDetail.source.documentationUrl} target="_blank" rel="noreferrer">
                            {selectedDatasetDetail.source.documentationUrl}
                          </a>
                        </div>
                      ) : null}
                      <div>
                        <span>Script / job</span>
                        <strong className="warehouse-mono">{selectedDatasetDetail.source.pythonFile || '—'}</strong>
                      </div>
                      <div>
                        <span>Cron wrapper</span>
                        <strong className="warehouse-mono">{selectedDatasetDetail.source.cronCommand || '—'}</strong>
                      </div>
                      <div>
                        <span>Latest run log</span>
                        <strong className="warehouse-mono">{latestRecentRun?.logFileLocation || '—'}</strong>
                      </div>
                      <div>
                        <span>Latest raw payload</span>
                        <strong className="warehouse-mono">{latestRecentRun?.rawResponseLocation || '—'}</strong>
                      </div>
                      <div>
                        <span>Latest source hash</span>
                        <strong className="warehouse-mono">{latestRecentRun?.sourceFileHash || '—'}</strong>
                      </div>
                      <div>
                        <span>Source notes</span>
                        <strong>{selectedDatasetDetail.source.freshnessNotes || selectedDatasetDetail.source.notes || '—'}</strong>
                      </div>
                    </div>
                  </section>

                  <section className="warehouse-detail-section">
                    <h3>Warnings & Notes</h3>
                    {selectedDatasetDetail.warningItems?.length ? (
                      <ul className="warehouse-warning-list">
                        {selectedDatasetDetail.warningItems.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="warehouse-empty-note">No active warnings recorded for the recent runs shown here.</p>
                    )}
                  </section>

                  <section className="warehouse-detail-section">
                    <h3>Database Tables Affected</h3>
                    {selectedDatasetDetail.source.targetTables?.length ? (
                      <div className="warehouse-table-pill-list">
                        {selectedDatasetDetail.source.targetTables.map((tableName) => (
                          <span key={tableName} className="warehouse-table-pill">{tableName}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="warehouse-empty-note">No target tables recorded yet.</p>
                    )}
                  </section>

                  {!Array.isArray(selectedDatasetDetail.recentChanges) ? (
                    <section className="warehouse-detail-section">
                      <h3>Recent Runs</h3>
                      {selectedDatasetDetail.recentRuns?.length ? (
                      <div className="warehouse-mini-table-wrap">
                        <table className="warehouse-mini-table">
                          <thead>
                            <tr>
                              <th>Status</th>
                              <th>Finished</th>
                              <th>Source Period</th>
                              <th>Rows</th>
                              <th>Regions</th>
                              <th>Warnings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDatasetDetail.recentRuns.map((run) => (
                              <tr key={run.runId}>
                                <td><WarehouseBadge label={run.statusLabel || run.status} status={run.status} /></td>
                                <td>{formatDateTime(run.finishedAt)}</td>
                                <td>{formatDate(run.latestSourcePeriod)}</td>
                                <td>{formatNumber(run.rowsPulled)} / {formatNumber(run.rowsWritten)}</td>
                                <td>{run.regionsLoaded == null ? '—' : run.regionsRequested == null ? formatNumber(run.regionsLoaded) : `${formatNumber(run.regionsLoaded)} / ${formatNumber(run.regionsMissing)}`}</td>
                                <td>{run.warningItems?.length ? run.warningItems[0] : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      ) : (
                        <p className="warehouse-empty-note">No run history recorded yet.</p>
                      )}
                    </section>
                  ) : null}

                  {Array.isArray(selectedDatasetDetail.recentChanges) ? (
                    <section className="warehouse-detail-section">
                      <h3>Recent Source Changes</h3>
                      {selectedDatasetDetail.recentChanges.length ? (
                      <div className="warehouse-mini-table-wrap">
                        <table className="warehouse-mini-table">
                          <thead><tr><th>Finished</th><th>Source period</th><th>Status</th></tr></thead>
                          <tbody>
                            {selectedDatasetDetail.recentChanges.map((run) => (
                              <tr key={`${run.runId}-source-change`}>
                                <td>{formatDateTime(run.finishedAt)}</td>
                                <td>{formatDate(run.latestSourcePeriod)}</td>
                                <td><WarehouseBadge label={run.statusLabel || run.status} status={run.status} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      ) : <p className="warehouse-empty-note">No source changes recorded in the available audit history.</p>}
                    </section>
                  ) : null}

                  {selectedDatasetDetail.latestRunIssues?.length ? (
                    <section className="warehouse-detail-section">
                      <h3>Latest Run Issues</h3>
                      <div className="warehouse-mini-table-wrap">
                        <table className="warehouse-mini-table">
                          <thead>
                            <tr>
                              <th>Region</th>
                              <th>Status</th>
                              <th>Source Period</th>
                              <th>Issue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDatasetDetail.latestRunIssues.map((issue) => (
                              <tr key={`${issue.regionKey}-${issue.status}`}>
                                <td>{issue.regionName || issue.regionKey || '—'}</td>
                                <td><WarehouseBadge label={issue.status} status={issue.status} /></td>
                                <td>{formatDate(issue.latestSourcePeriod)}</td>
                                <td>{issue.errorMessage || issue.warningMessage || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}

                  {selectedDatasetDetail.singleSheetAudits?.length ? (
                    <section className="warehouse-detail-section">
                      <h3>Zillow Sheet Audits</h3>
                      <div className="warehouse-mini-table-wrap">
                        <table className="warehouse-mini-table">
                          <thead>
                            <tr>
                              <th>Dataset</th>
                              <th>Status</th>
                              <th>Rows</th>
                              <th>Latest Period</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDatasetDetail.singleSheetAudits.map((audit) => (
                              <tr key={audit.datasetName}>
                                <td>{audit.datasetName}</td>
                                <td><WarehouseBadge label={audit.status} status={audit.status} /></td>
                                <td>{formatNumber(audit.rowCount)}</td>
                                <td>{formatDate(audit.maxDate)}</td>
                                <td>{audit.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}

                  {selectedDatasetDetail.liveStageParityAudits?.length ? (
                    <section className="warehouse-detail-section">
                      <h3>Live Raw vs Stage Parity</h3>
                      <p className="warehouse-empty-note">
                        These checks compare the current live raw Zillow tables against the current live stage tables. This is the source-of-truth check for silent stage drift and stale live snapshots.
                      </p>
                      <div className="warehouse-mini-table-wrap">
                        <table className="warehouse-mini-table">
                          <thead>
                            <tr>
                              <th>Dataset</th>
                              <th>Status</th>
                              <th>Regions</th>
                              <th>Rows</th>
                              <th>Latest Coverage</th>
                              <th>Date Span</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDatasetDetail.liveStageParityAudits.map((audit) => (
                              <tr key={audit.datasetName}>
                                <td>{audit.datasetLabel || audit.datasetName}</td>
                                <td><WarehouseBadge label={audit.status} status={audit.status} /></td>
                                <td>
                                  {audit.stageRegionCount == null || audit.rawRegionCount == null
                                    ? '—'
                                    : `${formatNumber(audit.stageRegionCount)} / ${formatNumber(audit.rawRegionCount)}`}
                                </td>
                                <td>
                                  {audit.stageRowCount == null || audit.rawRowCount == null
                                    ? '—'
                                    : `${formatNumber(audit.stageRowCount)} / ${formatNumber(audit.rawRowCount)}`}
                                </td>
                                <td>
                                  {audit.latestPeriodMetricCoveragePct == null
                                    ? '—'
                                    : `${audit.latestPeriodMetricCoveragePct}%`}
                                </td>
                                <td>
                                  {audit.stageMinDate && audit.stageMaxDate
                                    ? `${formatDate(audit.stageMinDate)} to ${formatDate(audit.stageMaxDate)}`
                                    : '—'}
                                </td>
                                <td>{audit.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}

                  {selectedDatasetDetail.rankedMarketCoverageSummary?.marketCount ? (
                    <section className="warehouse-detail-section">
                      <h3>Ranked Market Chart Coverage</h3>
                      <p className="warehouse-empty-note">
                        These checks verify how many current ranked metro pages have enough matched home-and-rent history to render the live 1% detail chart correctly.
                      </p>

                      <div className="warehouse-stat-grid">
                        <div className="warehouse-stat-card">
                          <span>0 rule months</span>
                          <strong>{formatNumber(selectedDatasetDetail.rankedMarketCoverageSummary.ruleMonthsZero)}</strong>
                        </div>
                        <div className="warehouse-stat-card">
                          <span>1 rule month</span>
                          <strong>{formatNumber(selectedDatasetDetail.rankedMarketCoverageSummary.ruleMonthsOne)}</strong>
                        </div>
                        <div className="warehouse-stat-card">
                          <span>2-35 rule months</span>
                          <strong>{formatNumber(selectedDatasetDetail.rankedMarketCoverageSummary.ruleMonthsTwoTo35)}</strong>
                        </div>
                        <div className="warehouse-stat-card">
                          <span>36+ rule months</span>
                          <strong>{formatNumber(selectedDatasetDetail.rankedMarketCoverageSummary.ruleMonths36Plus)}</strong>
                        </div>
                      </div>

                      {selectedDatasetDetail.rankedMarketCoverageSummary.topWindows?.length ? (
                        <div className="warehouse-mini-table-wrap">
                          <table className="warehouse-mini-table">
                            <thead>
                              <tr>
                                <th>Rank Window</th>
                                <th>0 Months</th>
                                <th>1 Month</th>
                                <th>&lt; 36 Months</th>
                                <th>36+ Months</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedDatasetDetail.rankedMarketCoverageSummary.topWindows.map((window) => (
                                <tr key={window.label}>
                                  <td>{window.label}</td>
                                  <td>{formatNumber(window.ruleMonthsZero)}</td>
                                  <td>{formatNumber(window.ruleMonthsOne)}</td>
                                  <td>{formatNumber(window.ruleMonthsUnder36)}</td>
                                  <td>{formatNumber(window.ruleMonths36Plus)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : null}

                      {rankedMarketCoverageAudits.length ? (
                        <div className="warehouse-mini-table-wrap">
                          {rankedMarketCoverageIssues.length ? (
                            <table className="warehouse-mini-table">
                              <thead>
                                <tr>
                                  <th>Rank</th>
                                  <th>Market</th>
                                  <th>Status</th>
                                  <th>Rule / Home / Rent</th>
                                  <th>Latest Rule Month</th>
                                  <th>Reason</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rankedMarketCoverageIssues
                                  .slice(0, 40)
                                  .map((audit) => (
                                    <tr key={`${audit.marketRank}-${audit.regionId}`}>
                                      <td>#{formatNumber(audit.marketRank)}</td>
                                      <td>{audit.marketLabel || '—'}</td>
                                      <td><WarehouseBadge label={audit.status} status={audit.status} /></td>
                                      <td>
                                        {`${formatNumber(audit.ruleMonthsLoaded)} / ${formatNumber(audit.homeMonthsLoaded)} / ${formatNumber(audit.rentMonthsLoaded)}`}
                                      </td>
                                      <td>{formatDate(audit.latestRulePeriod)}</td>
                                      <td>{audit.reason}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="warehouse-empty-note">
                              All currently ranked metro pages have at least 36 matched rent/home months live in the detail chart.
                            </p>
                          )}
                        </div>
                      ) : null}
                    </section>
                  ) : null}

                  {selectedDatasetDetail.postJoinAudits?.length ? (
                    <section className="warehouse-detail-section">
                      <h3>Zillow Join Audits</h3>
                      <p className="warehouse-empty-note">
                        Base row coverage compares joined rows against the base home-price table. Metric coverage measures non-null values inside the matched join rows.
                      </p>
                      <div className="warehouse-mini-table-wrap">
                        <table className="warehouse-mini-table">
                          <thead>
                            <tr>
                              <th>Join</th>
                              <th>Status</th>
                              <th>Base Row Coverage</th>
                              <th>Metric Coverage</th>
                              <th>Rows</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedDatasetDetail.postJoinAudits.map((audit) => (
                              <tr key={audit.joinName}>
                                <td>{audit.joinName}</td>
                                <td><WarehouseBadge label={audit.status} status={audit.status} /></td>
                                <td>{audit.baseRowCoveragePct == null ? '—' : `${audit.baseRowCoveragePct}%`}</td>
                                <td>{audit.metricCoveragePct == null ? '—' : `${audit.metricCoveragePct}%`}</td>
                                <td>
                                  {audit.joinTableRows == null || audit.baseTableRows == null
                                    ? '—'
                                    : `${formatNumber(audit.joinTableRows)} / ${formatNumber(audit.baseTableRows)}`}
                                </td>
                                <td>{audit.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ) : null}
                </div>
              ) : null}
            </aside>
          </div>
        ) : null}
      </div>
    </div>
  );
}
